import React, { useState } from 'react';
import { motion } from 'motion/react';
import { useContactForm } from '../hooks/useContactForm';
import { PROJECT_TYPES } from '../schemas/contactSchema';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Textarea from '../../../components/ui/Textarea';
import Button from '../../../components/ui/Button';
import Toast from '../../../components/ui/Toast';
import { Sparkles, Zap } from 'lucide-react';

const ContactForm = () => {
  const [isSimpleMode, setIsSimpleMode] = useState(true);
  const { form, isSubmitting, submitStatus, onSubmit, resetStatus } = useContactForm();
  const { register, handleSubmit, formState: { errors } } = form;

  const projectTypeOptions = Object.entries(PROJECT_TYPES).map(([value, label]) => ({
    value,
    label,
  }));


  const containerAnimation = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <>
      <motion.form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full p-6 space-y-6"
        variants={containerAnimation}
        initial="hidden"
        animate="show"
      >
        <motion.div variants={itemAnimation}>
          <div className="text-center mb-6">
            <motion.div
              className="inline-flex p-1 rounded-xl bg-muted border border-border mb-6"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring" }}
            >
              <button
                type="button"
                onClick={() => setIsSimpleMode(true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isSimpleMode
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  간단 문의
                </span>
              </button>
              <button
                type="button"
                onClick={() => setIsSimpleMode(false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  !isSimpleMode
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  상세 문의
                </span>
              </button>
            </motion.div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {isSimpleMode ? '빠른 프로젝트 문의' : '상세 프로젝트 문의'}
            </h2>
            <p className="text-muted-foreground">
              {isSimpleMode 
                ? '기본 정보만 입력하세요' 
                : '프로젝트에 대해 자세히 알려주세요'
              }
            </p>
          </div>
        </motion.div>

        {isSimpleMode ? (
          <>
            <motion.div variants={itemAnimation}>
              <Input
                label="담당자명"
                required
                {...register('contactPerson')}
                error={errors.contactPerson}
                placeholder="홍길동"
                className=""
              />
            </motion.div>
            <motion.div variants={itemAnimation}>
              <Input
                label="연락처"
                type="tel"
                required
                {...register('phone')}
                error={errors.phone}
                placeholder="010-1234-5678"
                className=""
              />
            </motion.div>
            <motion.div variants={itemAnimation}>
              <Textarea
                label="프로젝트 내용"
                required
                rows={4}
                {...register('description')}
                error={errors.description}
                placeholder="프로젝트에 대해 간단히 설명해주세요"
                className=""
              />
            </motion.div>
          </>
        ) : (
          <>
            <motion.div variants={itemAnimation} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="회사명"
                required
                {...register('companyName')}
                error={errors.companyName}
                placeholder="(주)날다"
                className=""
              />
              <Input
                label="담당자명"
                required
                {...register('contactPerson')}
                error={errors.contactPerson}
                placeholder="홍길동"
                className=""
              />
            </motion.div>

            <motion.div variants={itemAnimation} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="이메일"
                type="email"
                required
                {...register('email')}
                error={errors.email}
                placeholder="example@company.com"
                className=""
              />
              <Input
                label="연락처"
                type="tel"
                required
                {...register('phone')}
                error={errors.phone}
                placeholder="010-1234-5678"
                className=""
              />
            </motion.div>

            <motion.div variants={itemAnimation} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="프로젝트 유형"
                required
                options={projectTypeOptions}
                {...register('projectType')}
                error={errors.projectType}
                className="bg-muted/30 border-border/50 text-foreground"
              />
              <div className="relative">
                <Input
                  label="최대 예산"
                  type="number"
                  required
                  {...register('budget')}
                  error={errors.budget}
                  placeholder="1000"
                  className="pr-12"
                />
                <span className="absolute right-3 top-[38px] text-sm text-muted-foreground">만원</span>
              </div>
            </motion.div>

            <motion.div variants={itemAnimation}>
              <Input
                label="프로젝트 일정"
                required
                {...register('timeline')}
                error={errors.timeline}
                placeholder="예: 2025년 1월 ~ 3월"
                className=""
              />
            </motion.div>

            <motion.div variants={itemAnimation}>
              <Textarea
                label="프로젝트 설명"
                required
                rows={6}
                {...register('description')}
                error={errors.description}
                placeholder="프로젝트에 대해 자세히 설명해주세요. (최소 10자 이상)"
                className=""
              />
            </motion.div>
          </>
        )}

        <motion.div variants={itemAnimation} className="pt-4">
          <Button
            type="submit"
            size="lg"
            loading={isSubmitting}
            disabled={isSubmitting}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all"
          >
            {isSubmitting ? '전송 중...' : '문의 전송하기'}
          </Button>
          <p className="text-center text-xs text-muted-foreground mt-4">
            제출하신 정보는 안전하게 보호되며, 오직 프로젝트 문의 목적으로만 사용됩니다.
          </p>
        </motion.div>
      </motion.form>

      <Toast
        isVisible={submitStatus === 'success'}
        type="success"
        message="문의가 성공적으로 전송되었습니다. 빠른 시일 내에 연락드리겠습니다."
        onClose={resetStatus}
      />

      <Toast
        isVisible={submitStatus === 'error'}
        type="error"
        message="문의 전송에 실패했습니다. 잠시 후 다시 시도해주세요."
        onClose={resetStatus}
      />
    </>
  );
};

export default ContactForm;